import { createContext, useContext, useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import axios from 'axios'
// Lot Cartes (revue codex P2 sur #262, plan 12/09 D5) : le cache des couches réservées suit la
// session — vidé à la déconnexion, au changement de compte et quand le jeton est rejeté. Module
// sans dépendance lourde (pas de MapLibre) : rien de plus dans le bundle principal.
import { sujetDuJeton, viderCacheCartes } from './obs/carte/cache'

const API_BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api'

export interface User {
  email: string
  role: string
  name: string
  commune_reference?: string | null
  commune_role?: string | null
  thematiques_autorisees?: string[] | null
  // Droit « carte nationale » (CDC G, lot L1), lu en base par /auth/me : ouvre /sig (droitsCarte.ts).
  // null = la lecture du droit a échoué côté serveur (inconnu : traité comme absent).
  carte_nationale?: boolean | null
  /** Personnalisation de la mention d'export, distincte des droits d'accès. */
  mention_libre?: boolean
  // L'espace client du compte (CDC H, compte intercommunal), lu en base : la connexion d'un
  // compte d'intercommunalité mène à la vue de SON EPCI. N'ouvre rien : l'API revérifie.
  espace?: EspaceCompte | null
  // Lot 0 : l'adresse a-t-elle été confirmée (lu en base par /auth/me) ? Affiche l'avis
  // « confirmez votre adresse » ; n'ouvre rien, l'API revérifie.
  // null = la lecture a échoué côté serveur (inconnu : pas d'avis).
  email_verifie?: boolean | null
}

export interface EspaceCompte {
  type: 'commune' | 'epci' | 'organisme'; code: string | null; nom: string
  // Le rôle du compte dans SON espace (H-17) : seul l'administrateur de l'espace intercommunal
  // voit « Comptes & accès » et la gestion des membres. N'ouvre rien : l'API revérifie en base.
  role?: 'admin' | 'executif' | 'agent' | null
}

/** L'administrateur de l'espace de CETTE intercommunalité (CDC H, H-17) — pour l'affichage
    seulement : `/comptes`, `designer-admin` et `/api/espace/membres` revérifient en base. */
export function estAdministrateurEpci(u: Pick<User, 'espace'> | null | undefined, siren: string): boolean {
  const e = u?.espace
  return !!e && e.type === 'epci' && e.code === siren && e.role === 'admin'
}

/** La page d'arrivée d'un compte connecté : administration, SES études (consultant, rôle
    `analyst`), SON intercommunalité, SA commune, ou la recherche. */
export function destinationApresConnexion(u: Pick<User, 'role' | 'commune_reference' | 'espace'>): string {
  if (u.role === 'admin' || u.role === 'direction') return '/admin'
  if (u.role === 'analyst') return '/etudes'
  if (u.espace?.type === 'epci' && u.espace.code && /^\d{9}$/.test(u.espace.code)) {
    return `/o/epci/${u.espace.code}/synthese`
  }
  if (u.commune_reference) return `/o/${u.commune_reference}/synthese`
  return '/#recherche'
}

export interface DefiMfa {
  defi: string
  mfa_requise?: true
  enrolement_requis?: true
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<User | DefiMfa>
  setSession: (token: string, user: User) => void
  // Relit le compte en base (/auth/me) après un geste qui change son espace — le rattachement à
  // une invitation (revue codex P2 de #535) : sinon la session garde l'espace d'avant.
  rafraichirSession: () => Promise<void>
  logout: () => void
  isAdmin: boolean
  isAuthenticated: boolean
  // true tant que /auth/me est en vol au boot : les gardes de route DOIVENT attendre
  // (sinon un refresh sur /admin ou /espace redirige vers / avant que le rôle soit connu).
  loading: boolean
  // SE-3 (CDC GBM du 27/10) : la session ouverte dans cette page a expiré ; `AvisSession` propose
  // de se reconnecter. Remis à faux par une connexion, une déconnexion ou « Fermer ».
  sessionExpiree: boolean
  oublierSessionExpiree: () => void
  // SE-1 : /auth/me ne répond pas (réseau, délai, erreur serveur) ; la session est gardée, un
  // nouvel essai est programmé.
  connexionInterrompue: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

/** SE-1 : les délais entre deux lectures de /auth/me qui n'ont pas abouti (le dernier se répète). */
export const DELAIS_REESSAI_MS = [2000, 5000, 10000, 20000, 30000]
/** Le serveur garde la session après l'échéance lue dans le jeton (horloge du poste en avance) :
    on le relit à ce rythme. */
const RELECTURE_APRES_ECHEANCE_MS = 60_000
const DELAI_MAX_MINUTERIE_MS = 2 ** 31 - 1

const statutHttp = (e: unknown) => (e as { response?: { status?: number } })?.response?.status
/** La requête refusée portait-elle le jeton ENCORE en place ? Le stockage est partagé entre les
    onglets : un autre onglet a pu se reconnecter, et son jeton ne se retire pas sur un vieux 401
    (revue Codex P2). */
const refusDuJetonCourant = (e: unknown, token: string | null) => {
  const envoye = (e as { config?: { headers?: { Authorization?: unknown } } })?.config?.headers?.Authorization
  return !!token && envoye === `Bearer ${token}` && localStorage.getItem('lobster_token') === token
}

// Attache (ou retire) le JWT sur TOUS les appels axios. C'est le correctif central :
// AdminDashboard / EspaceClient n'envoyaient JAMAIS le token → 401 silencieux, espaces
// non fonctionnels en prod. Un seul point d'injection couvre tous les appels.
function applyAuthHeader(token: string | null) {
  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  else delete axios.defaults.headers.common['Authorization']
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('lobster_token'))
  const [loading, setLoading] = useState<boolean>(() => !!localStorage.getItem('lobster_token'))
  // Numéro de la dernière lecture de /auth/me : une réponse plus ANCIENNE qu'une lecture lancée
  // depuis (rafraichirSession) ne l'écrase jamais (revue codex P2 de #535).
  const lecture = useRef(0)
  // Une session a été ouverte DANS cette page (compte lu, ou connexion) : seule sa fin mérite
  // l'avis « Votre session a expiré » ; un jeton de la veille retrouvé au chargement se ferme
  // en silence (SE-3).
  const sessionVue = useRef(false)
  const reessai = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [sessionExpiree, setSessionExpiree] = useState(false)
  const [connexionInterrompue, setConnexionInterrompue] = useState(false)

  // La session est refusée pour de bon : jeton retiré, cache des cartes vidé (lot Cartes, D5).
  const fermerSession = () => {
    ++lecture.current
    clearTimeout(reessai.current)
    localStorage.removeItem('lobster_token')
    applyAuthHeader(null)
    if (sessionVue.current) setSessionExpiree(true)
    sessionVue.current = false
    setToken(null)
    setUser(null)
    setLoading(false)
    setConnexionInterrompue(false)
    void viderCacheCartes()
  }

  // Lit le compte (/auth/me). SE-1 (CDC GBM du 27/10) : seul un refus net (401 : jeton invalide,
  // expiré ou révoqué) ferme la session. Un réseau coupé, le délai de 8 s dépassé ou une erreur
  // serveur (redémarrage pendant un déploiement) la gardent : avis discret, nouvel essai de plus
  // en plus espacé. `loading` reste vrai tant que le compte n'a jamais été lu : une route gardée
  // attend au lieu de renvoyer à tort à l'accueil (revue Forge 10/07 : l'avis dit pourquoi).
  // Rend vrai si le compte a été lu.
  const lireCompte = (essai = 0): Promise<boolean> => {
    clearTimeout(reessai.current)
    const n = ++lecture.current
    return axios.get(`${API_BASE_URL}/auth/me`, { timeout: 8000 })
      .then(res => {
        if (n !== lecture.current) return false
        sessionVue.current = true
        setUser(res.data)
        setConnexionInterrompue(false)
        setLoading(false)
        return true
      })
      .catch((e: unknown) => {
        if (n !== lecture.current) return false
        if (statutHttp(e) === 401) {
          // Un autre onglet reconnecté a mis son jeton en place : on ne le retire pas, on l'adopte,
          // sans rien garder de l'ancien compte (peut-être un autre compte : revue Codex P2) ; les
          // routes réservées attendent la lecture du nouveau, que l'effet du jeton lance.
          const stocke = localStorage.getItem('lobster_token')
          if (stocke === null || refusDuJetonCourant(e, stocke)) { fermerSession(); return false }
          sessionVue.current = false
          setUser(null)
          setLoading(true)
          void viderCacheCartes()
          setToken(stocke)
          return false
        }
        setConnexionInterrompue(true)
        if (sessionVue.current) setLoading(false)     // compte déjà connu : les gardes jugent avec lui
        reessai.current = setTimeout(() => { void lireCompte(essai + 1) },
          DELAIS_REESSAI_MS[Math.min(essai, DELAIS_REESSAI_MS.length - 1)])
        return false
      })
  }

  useEffect(() => {
    // SE-3 : un seul gestionnaire pour toute l'application. Un 401 sur le jeton EN COURS est une
    // session expirée quand le serveur le dit (`X-Session-Expiree` : session échue, révoquée, démo
    // fermée, fondateur sans double authentification). L'horloge du poste ne tranche pas : en
    // avance, elle ferait d'un 401 de droits ou d'un code incorrect une déconnexion (revue Codex
    // P2) ; l'échéance passée relit le compte (minuterie plus bas) et le serveur répond. Le
    // serveur distingue une session périmée d'un simple code MFA incorrect ; ignorer les réponses
    // d'un ancien jeton évite de fermer une nouvelle connexion.
    const id = axios.interceptors.response.use(r => r, erreur => {
      if (erreur.response?.status === 401 && erreur.response?.headers?.['x-session-expiree'] === '1'
          && refusDuJetonCourant(erreur, token)) {
        fermerSession()
      }
      return Promise.reject(erreur)
    })
    return () => axios.interceptors.response.eject(id)
  }, [token])

  useEffect(() => {
    applyAuthHeader(token)
    if (!token) {
      ++lecture.current          // une lecture encore en vol ne rouvre pas la session
      clearTimeout(reessai.current)
      setUser(null); setLoading(false); setConnexionInterrompue(false)
      return
    }
    setLoading(true)
    // Le token voyage dans l'en-tête Authorization (plus en query string → plus de fuite logs/Referer).
    void lireCompte()
    return () => clearTimeout(reessai.current)
  }, [token])

  useEffect(() => {
    // SE-3 : l'échéance du jeton passe page ouverte. On relit le compte une seconde après : le
    // serveur tranche (401 → l'avis). Tant que ce jeton reste en place (horloge du poste en
    // avance, ou relecture en panne puis rétablie), on le relit chaque minute, quel que soit le
    // résultat de la relecture précédente (revue Codex P2). Un 401 retire le jeton et coupe la
    // minuterie. Un jeton déjà échu au chargement : la lecture du montage s'en charge.
    const expMs = token ? sujetDuJeton(token)?.expMs : null
    if (!token || expMs == null) return
    let minuterie: ReturnType<typeof setTimeout> | undefined
    const armer = (delai: number) => {
      minuterie = setTimeout(() => {
        armer(RELECTURE_APRES_ECHEANCE_MS)
        void lireCompte()
      }, Math.min(delai, DELAI_MAX_MINUTERIE_MS))
    }
    const delai = expMs - Date.now() + 1000
    armer(delai > 0 ? delai : RELECTURE_APRES_ECHEANCE_MS)
    return () => clearTimeout(minuterie)
  }, [token])

  const setSession = (t: string, u: User) => {
    void viderCacheCartes()     // changement de compte : rien de l'ancien ne survit (lot Cartes, D5)
    localStorage.setItem('lobster_token', t)
    applyAuthHeader(t)
    sessionVue.current = true
    setSessionExpiree(false)
    setUser(u)
    setToken(t)
  }

  // Après un geste qui change l'espace du compte : la même lecture, aux mêmes règles (SE-1).
  const rafraichirSession = async () => { await lireCompte() }

  const login = async (email: string, password: string): Promise<User | DefiMfa> => {
    const res = await axios.post(`${API_BASE_URL}/auth/login`, { email, password })
    // Un défi reste uniquement en mémoire dans la page de connexion : jamais un Bearer.
    if (res.data.mfa_requise || res.data.enrolement_requis) return res.data as DefiMfa
    const { token: t, role, name, commune_reference, carte_nationale, espace } = res.data
    const u: User = { email, role, name, commune_reference: commune_reference ?? null,
                      carte_nationale: carte_nationale === true, espace: espace ?? null }
    setSession(t, u)
    return u
  }

  const logout = () => {
    sessionVue.current = false    // déconnexion voulue : aucun avis d'expiration
    setSessionExpiree(false)
    localStorage.removeItem('lobster_token')
    applyAuthHeader(null)
    setToken(null)
    setUser(null)
    void viderCacheCartes()     // déconnexion : les couches réservées quittent le navigateur (lot Cartes, D5)
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'direction'
  const isAuthenticated = !!token

  return (
    <AuthContext.Provider value={{ user, token, login, setSession, rafraichirSession, logout, isAdmin, isAuthenticated, loading,
      sessionExpiree, oublierSessionExpiree: () => setSessionExpiree(false), connexionInterrompue }}>
      {children}
    </AuthContext.Provider>
  )
}

// Les cartes publiques peuvent être montées sans fournisseur de session (page intégrée).
export function useAuth(options: { facultatif: true }): AuthContextType | null
export function useAuth(): AuthContextType
export function useAuth(options?: { facultatif: true }) {
  const ctx = useContext(AuthContext)
  if (!ctx && !options?.facultatif) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
